import ExcelJS from "exceljs";
import { INDIAN_STATES } from "../employee-management/salary-structure/utils/ptStates";

export const MASTER_TEMPLATE_OPTIONS = [
  { key: "all", label: "All masters (one workbook)", sheetNames: null },
  { key: "branch", label: "Branch", sheetNames: ["Branch"] },
  { key: "department", label: "Department", sheetNames: ["Department"] },
  { key: "designation", label: "Designation", sheetNames: ["Designation"] },
  { key: "subDepartment", label: "Sub Department", sheetNames: ["Sub Department"] },
  { key: "grade", label: "Grade", sheetNames: ["Grade"] },
  { key: "category", label: "Category", sheetNames: ["Category"] },
  { key: "reasons", label: "Reasons", sheetNames: ["Reasons"] },
];

/** Sheet processing order when importing All (FK dependencies). */
export const MASTER_IMPORT_ORDER = [
  "Branch",
  "Department",
  "Grade",
  "Category",
  "Reasons",
  "Sub Department",
  "Designation",
];

const STATUS_VALUES = ["Active", "Inactive"];
const REASON_TYPES = ["Resignation", "Termination"];
const DATA_ROWS = 500;
const HEADER_FILL = "FFE8EEF5";
const SAMPLE_FILL = "FFF3F4F6";

const SHEET_DEFS = {
  Branch: {
    headers: ["Name*", "Address", "State*", "Status"],
    sample: ["Bengaluru", "MG Road", "Karnataka", "Active"],
    widths: [22, 36, 28, 12],
    rules: [
      "Name* — unique branch name (required).",
      "Address — optional.",
      "State* — must match an Indian state/UT from the dropdown (required).",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
  Department: {
    headers: ["Name*", "Status"],
    sample: ["Engineering", "Active"],
    widths: [24, 12],
    rules: [
      "Name* — unique department name (required).",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
  Designation: {
    headers: [
      "Name*",
      "Department*",
      "Salary Range From",
      "Salary Range To",
      "Experience",
      "Qualifications",
      "Job Description",
      "Status",
    ],
    sample: [
      "Software Engineer",
      "Engineering",
      300000,
      800000,
      "2-4 years",
      "B.E / B.Tech",
      "Build and maintain product features",
      "Active",
    ],
    widths: [22, 22, 18, 16, 14, 20, 36, 12],
    numberCols: [3, 4],
    rules: [
      "Name* — designation name (required; unique per department).",
      "Department* — must match an existing Department name exactly (or a row on the Department sheet when using All masters).",
      "Salary Range From / To — optional numbers (annual).",
      "Experience, Qualifications, Job Description — optional text.",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
  "Sub Department": {
    headers: ["Name*", "Department*", "Status"],
    sample: ["Platform", "Engineering", "Active"],
    widths: [22, 22, 12],
    rules: [
      "Name* — sub-department name (required; unique per department).",
      "Department* — must match an existing Department name exactly.",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
  Grade: {
    headers: ["Name*", "Status"],
    sample: ["L1", "Active"],
    widths: [18, 12],
    rules: [
      "Name* — unique grade name (required).",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
  Category: {
    headers: ["Name*", "Status"],
    sample: ["Skilled", "Active"],
    widths: [22, 12],
    rules: [
      "Name* — unique category name (required).",
      "Status — Active or Inactive (default Active if blank).",
      "Minimum wages (if enabled) are managed separately on Category → Import MW.",
    ],
  },
  Reasons: {
    headers: ["Name*", "Type*", "Status"],
    sample: ["Better opportunity", "Resignation", "Active"],
    widths: [28, 16, 12],
    rules: [
      "Name* — reason text (required; unique per Type).",
      "Type* — Resignation or Termination (dropdown).",
      "Status — Active or Inactive (default Active if blank).",
    ],
  },
};

function styleHeaderRow(sheet, colCount) {
  const row = sheet.getRow(1);
  row.height = 22;
  for (let c = 1; c <= colCount; c += 1) {
    const cell = sheet.getCell(1, c);
    cell.font = { bold: true, color: { argb: "FF1F2937" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    };
    cell.alignment = { vertical: "middle", wrapText: true, horizontal: "left" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
    };
  }
  sheet.views = [{ state: "frozen", ySplit: 1 }];
}

function styleSampleRow(sheet, colCount) {
  for (let c = 1; c <= colCount; c += 1) {
    const cell = sheet.getCell(2, c);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: SAMPLE_FILL },
    };
    cell.font = { italic: true, color: { argb: "FF4B5563" } };
  }
}

function addInlineListValidation(sheet, col, values = [], maxRows = DATA_ROWS) {
  const list = (values || []).filter(Boolean);
  if (!list.length) return;
  const formula = `"${list.join(",")}"`;
  if (formula.length > 250) return false;
  for (let r = 2; r <= maxRows + 1; r += 1) {
    sheet.getCell(r, col).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [formula],
      showErrorMessage: true,
      errorTitle: "Invalid value",
      error: `Choose one of: ${list.slice(0, 8).join(", ")}${list.length > 8 ? "…" : ""}`,
    };
  }
  return true;
}

function addRangeListValidation(sheet, col, formula, title, error, maxRows = DATA_ROWS) {
  for (let r = 2; r <= maxRows + 1; r += 1) {
    sheet.getCell(r, col).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [formula],
      showErrorMessage: true,
      errorTitle: title,
      error,
    };
  }
}

function formatMasterLine(label, values = []) {
  const list = (values || []).filter(Boolean);
  if (!list.length) return `${label}: (none configured — add in Configuration → Masters)`;
  if (list.length > 40) {
    return `${label}: ${list.slice(0, 40).join(", ")} … (+${list.length - 40} more — use dropdown on data sheet)`;
  }
  return `${label}: ${list.join(", ")}`;
}

function addInstructionsSheet(wb, { templateKey, sheetNames, departmentNames }) {
  const info = wb.addWorksheet("Instructions");
  info.getColumn(1).width = 110;

  const isAll = templateKey === "all";
  const title = isAll
    ? "PaceHRM — Enterprise Masters Bulk Import"
    : `PaceHRM — ${MASTER_TEMPLATE_OPTIONS.find((o) => o.key === templateKey)?.label || "Master"} Import`;

  const rules = [
    "1. Do not rename sheet names or column headers (columns marked * are required).",
    "2. Enter data on the master sheet(s) below. Row 2 is a sample — replace or delete it before upload.",
    "3. Use dropdowns where provided (State, Status, Type, Department).",
    "4. Status values: Active or Inactive (blank → Active).",
    "5. Save as .xlsx and upload from Import / Export → Configuration → Import Masters.",
  ];

  if (isAll) {
    rules.push(
      "6. Upload order (All workbook): Branch → Department → Grade → Category → Reasons → Sub Department → Designation.",
      "7. Designation / Sub Department Department* must match a Department name (existing or on the Department sheet)."
    );
  } else {
    rules.push(
      "6. Duplicate names (same master + company) are rejected — fix the file and re-upload."
    );
  }

  sheetNames.forEach((name) => {
    const def = SHEET_DEFS[name];
    if (!def?.rules?.length) return;
    rules.push("", `—— ${name} columns ——`, ...def.rules.map((r) => `• ${r}`));
  });

  const masters = [
    { label: "Status", values: STATUS_VALUES },
    { label: "Reasons Type", values: REASON_TYPES },
  ];
  if (sheetNames.includes("Branch")) {
    masters.push({ label: "State (dropdown)", values: INDIAN_STATES });
  }
  if (sheetNames.some((s) => s === "Designation" || s === "Sub Department")) {
    masters.push({
      label: "Department (must match exactly)",
      values: departmentNames.length ? departmentNames : ["(none yet — create Department first or include Department sheet)"],
    });
  }

  const lines = [title, "", ...rules, "", "—— Allowed values (must match exactly) ——", ""];
  masters.forEach(({ label, values }) => {
    lines.push(formatMasterLine(label, values));
  });
  lines.push(
    "",
    "Hidden sheet “Lists” holds long dropdown lists (State / Department) — do not delete it.",
    "Enter data on the master sheet(s). Save as .xlsx before upload."
  );

  lines.forEach((t, i) => {
    const cell = info.getCell(i + 1, 1);
    cell.value = t;
    cell.alignment = { wrapText: true, vertical: "top" };
    if (i === 0) cell.font = { bold: true, size: 14 };
    if (String(t).startsWith("——")) cell.font = { bold: true };
  });
}

function addDataSheet(wb, sheetName, { departmentNames = [] } = {}) {
  const def = SHEET_DEFS[sheetName];
  if (!def) return null;

  const sheet = wb.addWorksheet(sheetName);
  const headers = def.headers;
  headers.forEach((h, i) => {
    const cell = sheet.getCell(1, i + 1);
    cell.value = h;
    sheet.getColumn(i + 1).width =
      def.widths?.[i] || Math.min(36, Math.max(14, String(h).length + 2));
  });
  styleHeaderRow(sheet, headers.length);

  (def.sample || []).forEach((v, i) => {
    sheet.getCell(2, i + 1).value = v ?? "";
  });
  styleSampleRow(sheet, headers.length);

  (def.numberCols || []).forEach((col) => {
    for (let r = 2; r <= DATA_ROWS + 1; r += 1) {
      sheet.getCell(r, col).numFmt = "#,##0";
    }
  });

  return { sheet, def, departmentNames };
}

/**
 * Build ExcelJS workbook for one master type or all masters.
 * Formatted like employee import templates (Instructions + styled header + frozen panes).
 */
export async function buildMastersImportWorkbook(templateKey, { departmentNames = [] } = {}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  const deptList = [
    ...new Set((departmentNames || []).map((n) => String(n || "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  const sheetNames =
    templateKey === "all"
      ? MASTER_IMPORT_ORDER
      : MASTER_TEMPLATE_OPTIONS.find((o) => o.key === templateKey)?.sheetNames || [];

  addInstructionsSheet(wb, { templateKey, sheetNames, departmentNames: deptList });

  const lists = wb.addWorksheet("Lists");
  lists.getColumn(1).values = ["State", ...INDIAN_STATES];
  lists.getColumn(2).values = ["Status", ...STATUS_VALUES];
  lists.getColumn(3).values = ["ReasonType", ...REASON_TYPES];
  lists.getColumn(4).values = [
    "Department",
    ...(deptList.length ? deptList : ["Engineering"]),
  ];
  lists.state = "hidden";
  lists.getColumn(1).width = 36;
  lists.getColumn(4).width = 28;

  const stateEnd = INDIAN_STATES.length + 1;
  const deptEnd = Math.max(2, (deptList.length || 1) + 1);

  sheetNames.forEach((sheetName) => {
    const added = addDataSheet(wb, sheetName, { departmentNames: deptList });
    if (!added) return;
    const { sheet } = added;

    if (sheetName === "Branch") {
      addRangeListValidation(
        sheet,
        3,
        `Lists!$A$2:$A$${stateEnd}`,
        "State",
        "Select a state from the list"
      );
      addInlineListValidation(sheet, 4, STATUS_VALUES);
    } else if (sheetName === "Department" || sheetName === "Grade" || sheetName === "Category") {
      addInlineListValidation(sheet, 2, STATUS_VALUES);
    } else if (sheetName === "Designation") {
      addRangeListValidation(
        sheet,
        2,
        `Lists!$D$2:$D$${deptEnd}`,
        "Department",
        "Select a department from the list"
      );
      addInlineListValidation(sheet, 8, STATUS_VALUES);
    } else if (sheetName === "Sub Department") {
      addRangeListValidation(
        sheet,
        2,
        `Lists!$D$2:$D$${deptEnd}`,
        "Department",
        "Select a department from the list"
      );
      addInlineListValidation(sheet, 3, STATUS_VALUES);
    } else if (sheetName === "Reasons") {
      addInlineListValidation(sheet, 2, REASON_TYPES);
      addInlineListValidation(sheet, 3, STATUS_VALUES);
    }
  });

  return wb;
}

export async function downloadMastersImportTemplate(templateKey, ctx = {}) {
  const wb = await buildMastersImportWorkbook(templateKey, ctx);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const opt = MASTER_TEMPLATE_OPTIONS.find((o) => o.key === templateKey);
  const slug =
    templateKey === "all" ? "All_Masters" : (opt?.label || templateKey).replace(/\s+/g, "_");
  a.download = `PaceHRM_${slug}_Import_Template.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export function normalizeStatus(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s || s === "active") return "active";
  if (s === "inactive") return "inactive";
  return null;
}

export function normalizeReasonType(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "resignation" || s === "termination") return s;
  return null;
}

export function matchIndianState(raw) {
  const s = String(raw || "").trim();
  if (!s) return { ok: false, value: "" };
  const hit = INDIAN_STATES.find((st) => st.toLowerCase() === s.toLowerCase());
  if (hit) return { ok: true, value: hit };
  return { ok: false, value: s };
}

function cellText(row, col) {
  const cell = row.getCell(col);
  const v = cell.text != null && String(cell.text).trim() !== "" ? cell.text : cell.value;
  if (v == null) return "";
  if (typeof v === "object" && v.text) return String(v.text).trim();
  if (typeof v === "object" && v.result != null) return String(v.result).trim();
  return String(v).trim();
}

function cellNumber(row, col) {
  const raw = row.getCell(col).value;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return raw;
  const n = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function headerKey(text) {
  return String(text || "")
    .replace(/\*$/, "")
    .trim()
    .toLowerCase();
}

/** True if row 1 looks like a header (not a sample data row). */
function rowLooksLikeHeader(row, expectedFirst) {
  const first = headerKey(cellText(row, 1));
  return first === headerKey(expectedFirst) || first === "name";
}

/** Parse workbook into { sheetName → rows[] } (data rows only). */
export async function parseMastersWorkbook(arrayBuffer) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const bySheet = {};

  const wanted = new Set(MASTER_IMPORT_ORDER);
  wb.worksheets.forEach((ws) => {
    const name = String(ws.name || "").trim();
    if (!wanted.has(name) || name === "Lists" || name === "Instructions") return;
    const rows = [];
    let headerRow = 1;
    const firstRow = ws.getRow(1);
    if (!rowLooksLikeHeader(firstRow, "Name*")) {
      // Unusual file — still try from row 1 as data
      headerRow = 0;
    }

    ws.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRow) return;
      if (name === "Branch") {
        const Name = cellText(row, 1);
        if (!Name || headerKey(Name) === "name") return;
        rows.push({
          _row: rowNumber,
          name: Name,
          address: cellText(row, 2),
          state: cellText(row, 3),
          status: cellText(row, 4),
        });
      } else if (name === "Department" || name === "Grade" || name === "Category") {
        const Name = cellText(row, 1);
        if (!Name || headerKey(Name) === "name") return;
        rows.push({ _row: rowNumber, name: Name, status: cellText(row, 2) });
      } else if (name === "Designation") {
        const Name = cellText(row, 1);
        if (!Name || headerKey(Name) === "name") return;
        rows.push({
          _row: rowNumber,
          name: Name,
          department: cellText(row, 2),
          salaryRangeFrom: cellNumber(row, 3),
          salaryRangeTo: cellNumber(row, 4),
          experience: cellText(row, 5),
          qualifications: cellText(row, 6),
          jobDescription: cellText(row, 7),
          status: cellText(row, 8),
        });
      } else if (name === "Sub Department") {
        const Name = cellText(row, 1);
        if (!Name || headerKey(Name) === "name") return;
        rows.push({
          _row: rowNumber,
          name: Name,
          department: cellText(row, 2),
          status: cellText(row, 3),
        });
      } else if (name === "Reasons") {
        const Name = cellText(row, 1);
        if (!Name || headerKey(Name) === "name") return;
        rows.push({
          _row: rowNumber,
          name: Name,
          type: cellText(row, 2),
          status: cellText(row, 3),
        });
      }
    });
    bySheet[name] = rows;
  });

  return bySheet;
}
